/*
 * Copyright (c) 2023-2025 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { defaultError } from "./RequestUtil";
import {
  parseAllAccountsResponse,
  parseAllClientResponse,
  parseCaptivePortalAllImageResponse,
  parseCaptivePortalAllLayoutResponse,
  parseCaptivePortalLayoutDeleteResponse,
  parseCaptivePortalLayoutResponse,
  parseCaptivePortalResponse,
  parseClientResponse,
  parseClientStatsResponse,
  parseClientUsersResponse,
  parseDashboardLoginResponse,
  parseNetworkLoginPageResponse,
  parseNetworkVerifyResponse,
  parseRouterFileResponse,
  parseSaveCaptivePortalLayoutResponse,
  parseTemplateResponse,
  parseAllRouterStatusesResponse,
  parseRouterScriptFileResponse,
  parseServerFileResponse,
  parseRouterConfigurationResponse,
  parseIdResponse,
  parseRouterDisconnectUserResponse,
  parseSendgridConfigurations,
  parseSendgridSender,
  parseTwilioSmsConfigurations,
  parseClientEmailCannedMessage,
  parseClientSmsCannedMessage,
  parseMarketingCampaigns,
  parseMarketingCampaign,
  parseAdminDataResponse,
  parseAdminChartResponse,
  parseAdminAttentionResponse,
  parseAccountResponse,
  parseMailchimpConfiguration,
  parseMailchimpSyncStatus,
  parseZapierConfiguration,
  parseNetworkSmsVerifyResponse,
  parseReviewAutomationConfig,
  parseReviewAutomationPageConfigs,
  parseReviewAutomationReviews,
  parseReviewAutomationPageReviewUpdate,
  parseChannelAutoOptimizationResponse,
  parseReviewSearchVerify,
  parseExistsResponse,
  parseLocationAddressValid,
  parseAllMarketingBroadcasts,
  parseSendgridInfoResponse,
  parseTwilioInfoResponse,
  parseMailchimpInfoResponse,
  parseZapierInfoResponse,
  parseClientActivityResponse,
  parsePostLoginPreviewResponse,
  parseCaptivePortalGeneratedName,
  parseNetworkVerifyConnect,
  parseNetworkCodeVerify,
  parseMarketingBroadcasts,
  parseFilterMarketingBroadcastClientGuestsResponse,
  parseMarketingAutomations,
  parseClientSendgridSenders,
  parseMarketingHomeResponse,
  parseConstantContactConfiguration,
  parseConstantContactSyncStatus,
  parseRouterHttpsHotspotEnabledUpdateResponse,
  parseZapierCrms,
  parseMarketingTemplateResponse,
  parseMarketingAllTemplateResponse,
  parseMarketingTemplateNameResponse,
  parseAIGeneratedImage,
  parseRemoteManagerRouterResponse,
  parseProductAndServices,
  parseGdprRequiredResponse,
  parseMarketingNameRecommendation,
  parsePayPalBillingSubscriptionsRevise,
  parsePaypalPlans,
  parsePaypalPlan,
  parsePaypalDiscount,
  parsePaypalDiscounts,
  parseGuestProfileResponse,
  parseErrorMessage,
  parseIntegrationConfigurations,
  parseSendBroadcast,
  parseMarketingDefaultLimitResponse,
  parseMarketingClientLimitResponse,
  parseMarketingClientLimitSentResponse,
  parsePlivoSmsConfigurations,
  parseTurboSmsConfigurations,
  parseBranding,
  parseBrandingList,
} from "./RequestParse";
import { useLocalTimezone } from "../util/TimezoneList";
import { convertPostLoginType } from "../pages/loginPage/LoginPageUtil";
import { mergeDeep } from "../util/Object";

// helper functions
const responseHandler = (output, parse = null, args = {}) => {
  const { error, sessionExpired, response } = output;

  // return expiration if exists
  if (sessionExpired) {
    return { sessionExpired: sessionExpired };
  }

  // return error if exists
  if (error) {
    return { error: error };
  }

  // process results if handler available
  if (parse != null) {
    return parse({...args, response: response});
  }

  // no parsing available
  return {};
};

const navigationHandler = (results, navigate = null) => {
  return results ? navigate?.(results) : null;
}

const completionHandler = (results, completion = null) => {
  // run completion if provided
  if (completion) {
    const validResults = results[0] != null;
    completion?.(validResults ? results[0] : { error: defaultError });
  } else {
    const error = results[0]?.error;
    if (error) {
      alert("Request Error: " + error);
    }
  }
};

const completionHandlerAsync = (results, completion = null) => {
  // run completion if provided
  if (completion) {
    const validResults = results[0] != null;
    completion?.(validResults ? results : { error: defaultError });
  } else {
    const error = results[0]?.error;
    if (error) {
      alert("Request Error: " + error);
    }
  }
};

const mergeCompletionHandler = (results, completion = null) => {
  // run completion if provided
  if (completion) {
    const validResults = results[0] != null;
    completion?.(validResults ? (results.length === 1 ? results : mergeDeep({}, ...results)) : { error: defaultError });
  } else {
    const error = results[0]?.error;
    if (error) {
      alert("Request Error: " + error);
    }
  }
};

// requests
export const requestNetworkLoginPage = ({
  context,
  identity,
  macAddress,
  previewClientId,
  previewLayoutId,
  previewLocale,
  navigate,
  completion
}) => {
  const preview = previewClientId != null || previewLayoutId != null;
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: preview ? "captive_portal/login/preview/get" : "captive_portal/login/get",
        payload: preview ? {
          client_id: previewClientId,
          captive_portal_id: previewLayoutId
        } : {
          nas_client_identity: identity,
          mac_address: macAddress
        },
        handler: (results) => responseHandler(
          results,
          parseNetworkLoginPageResponse,
          { forcedLocale: previewLocale }
        )
      }]
    }
  });
};

export const requestNetworkConnect = ({
  context,
  identity,
  loginMethod,
  username,
  name,
  country,
  locale,
  birthday,
  email,
  phoneNumber,
  deviceMacAddress,
  gdprAccepted,
  customTextField,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "smart_wifi/network/user/connect",
        payload: {
          nas_client_identity: identity,
          type: loginMethod ?? "free_wifi",
          username: username,
          name: name,
          country: country,
          locale: locale,
          birthday: birthday,
          email: email,
          phone_number: phoneNumber,
          mac_address: deviceMacAddress,
          gdpr_accepted: gdprAccepted,
          custom_text_field: customTextField,
        },
        handler: (results) => responseHandler(results, parseNetworkVerifyConnect)
      }],
    }
  });
};

export const requestNetworkEmailCodeResend = ({
  context,
  email,
  identity,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "smart_wifi/network/user/email_confirmation/resend",
        payload: {
          email: email,
          nas_client_identity: identity
        },
        handler: (results) => responseHandler(results)
      }],
    }
  });
};

export const requestNetworkSmsCodeResend = ({
  context,
  phoneNumber,
  identity,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "smart_wifi/network/user/sms_confirmation/resend",
        payload: {
          phone_number: phoneNumber,
          nas_client_identity: identity
        },
        handler: (results) => responseHandler(results)
      }],
    }
  });
};

// export const requestNetworkUserVerify = ({
//   context,
//   code,
//   email,
//   identity,
//   resend = false,
//   navigate,
//   completion
// }) => {
//   context({
//     requestNavigate: {
//       handleNavigate: (results) => navigationHandler(results, navigate),
//       handleCompletion: (results) => completionHandler(results, completion),
//       showLoading: true,
//       requests: [{
//         route: resend ? "smart_wifi/network/user/email_confirmation/resend" : "smart_wifi/network/user/verify",
//         payload: {
//           code: code,
//           email: email,
//           nas_client_identity: identity
//         },
//         handler: (results) => responseHandler(results, parseNetworkVerifyResponse)
//       }],
//     }
//   });
// };

export const requestNetworkLogin = ({
  context,
  username,
  identity,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "smart_wifi/network/user/login",
        payload: {
          username: username,
          nas_client_identity: identity
        },
        handler: (results) => responseHandler(results, parseNetworkVerifyResponse)
      }],
    }
  });
};

export const requestNetworkVerify = ({
  context,
  identity,
  code,
  email,
  phoneNumber,
  deviceMacAddress,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "smart_wifi/network/user/verify",
        payload: {
          nas_client_identity: identity,
          code: code,
          email: email,
          phone_number: phoneNumber,
          mac_address: deviceMacAddress,
        },
        handler: (results) => responseHandler(results, parseNetworkCodeVerify)
      }],
    }
  });
};

export const requestNetworkGuestUpdateCache = ({
  context,
  identity,
  macAddress,
  locale,
  tosAccepted,
  gdprAccepted,
  timezone,
  showLoading = false,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "user/cache/update",
        payload: {
          nas_client_identity: identity,
          mac_address: macAddress,
          language: locale,
          tos_accepted: tosAccepted,
          gdpr_accepted: gdprAccepted,
          timezone: timezone,
        },
        handler: (results) => responseHandler(results)
      }],
    }
  });
};

export const requestNetworkFreeWifiLogin = ({
  context,
  username,
  identity,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "smart_wifi/free_wifi/login",
        payload: {
          username: username,
          nas_client_identity: identity
        },
        handler: (results) => responseHandler(results, parseNetworkVerifyResponse)
      }],
    }
  });
};

export const requestNetworkSmsResendCode = ({
  context,
  username,
  identity,
  navigate,
  completion
}) => {
  requestNetworkSmsLogin({
    context: context,
    username: username,
    password: null,
    identity: identity,
    navigate: navigate,
    completion: completion
  })
}

export const requestNetworkSmsLogin = ({
  context,
  username,
  password,
  identity,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "smart_wifi/network/user/sms/login",
        payload: {
          username: username,
          password: password,
          nas_client_identity: identity
        },
        handler: (results) => responseHandler(results, parseNetworkSmsVerifyResponse)
      }],
    }
  });
};

export const requestNetworkUserRegister = ({
  context,
  identity,
  username,
  password,
  email,
  firstName,
  lastName,
  gender,
  birthday,
  phoneNumber,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "smart_wifi/network/user/register",
        payload: {
          username: username,
          password: password,
          email: email ?? "",
          first_name: firstName ?? "",
          last_name: lastName ?? "",
          gender: gender ?? "",
          birthday: birthday,
          phone_number: phoneNumber,
          nas_client_identity: identity
        },
        handler: (results) => responseHandler(results, parseNetworkVerifyResponse)
      }]
    }
  });
};

export const requestNetworkSmsRegister = ({
  context,
  identity,
  username,
  email,
  firstName,
  lastName,
  gender,
  birthday,
  phoneNumber,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "smart_wifi/network/user/sms/register",
        payload: {
          username: username,
          email: email ?? "",
          first_name: firstName ?? "",
          last_name: lastName ?? "",
          gender: gender ?? "",
          birthday: birthday,
          phone_number: phoneNumber,
          nas_client_identity: identity
        },
        handler: (results) => responseHandler(results, parseNetworkSmsVerifyResponse)
      }]
    }
  });
};

export const requestNetworkUserVerify = ({
  context,
  code,
  email,
  identity,
  resend = false,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: resend ? "smart_wifi/network/user/email_confirmation/resend" : "smart_wifi/network/user/verify",
        payload: {
          code: code,
          email: email,
          nas_client_identity: identity
        },
        handler: (results) => responseHandler(results, parseNetworkVerifyResponse)
      }],
    }
  });
};

export const requestDashboardLogin = ({
  context,
  username,
  password,
  timezone,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "account/login",
        payload: { username, password, timezone },
        handler: (results) => responseHandler(results, parseDashboardLoginResponse)
      }]
    }
  });
};

export const requestDashboardLoginToken = ({
  context,
  token,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "account/login",
        payload: { token },
        handler: (results) => responseHandler(results, parseDashboardLoginResponse)
      }]
    }
  });
};

export const requestDashboardReLogin = ({
  context,
  token,
  timezone,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "account/relogin",
        payload: { token, timezone },
        handler: (results) => responseHandler(results, parseDashboardLoginResponse)
      }]
    }
  });
};

export const requestDashboardPasswordReset = ({
  context,
  email,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "account/password/reset",
        payload: { email: email },
        handler: (results) => responseHandler(results)
      }]
    }
  });
};

export const requestAdminDashboardData = ({
  context,
  token,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "admin/client/get/all",
        token: token,
        payload: {},
        handler: (results) => responseHandler(results, parseAdminDataResponse)
      }]
    }
  });
};

export const requestAdminChartRefresh = ({
  context,
  token,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "admin/client/activity/refresh",
        token: token,
        payload: {},
        handler: (results) => responseHandler(results, parseAdminChartResponse)
      }]
    }
  });
};

export const requestAdminAttentionRefresh = ({
  context,
  token,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "admin/client/needs_attention/refresh",
        token: token,
        payload: {},
        handler: (results) => responseHandler(results, parseAdminAttentionResponse)
      }]
    }
  });
};

export const requestClient = ({
  context,
  token,
  clientId,
  includeLocations,
  includeRouters,
  includeAccounts,
  includeImages,
  includeCaptivePortals,
  includeCaptivePortalLayouts,
  includeCaptivePortalSchedules,
  contextKey,
  key,
  settingsPage,
  showLoading = true,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "client/get",
        token: token,
        payload: {
          client_id: clientId,
          include_locations: includeLocations,
          include_nas_clients: includeRouters,
          include_accounts: includeAccounts,
          include_images: includeImages,
          include_captive_portals: includeCaptivePortals,
          include_captive_portal_layouts: includeCaptivePortalLayouts,
          include_captive_portal_schedules: includeCaptivePortalSchedules,
        },
        handler: (results) => responseHandler(
          results,
          parseClientResponse,
          { contextKey, key, settingsPage }
        )
      }]
    }
  });
};

// TODO: Add include flags if needed
export const requestAllClients = ({
  context,
  token,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "client/get/all",
        token: token,
        payload: {},
        handler: (results) => responseHandler(results, parseAllClientResponse)
      }],
      retries: 1
    }
  });
};

export const requestAllDashboardUserData = ({
  context,
  token,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => mergeCompletionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "client/get/all",
        token: token,
        payload: {},
        handler: (results) => responseHandler(results, parseAllClientResponse)
      },{
        route: "branding/get/all",
        token: token,
        handler: (results) => responseHandler(results, parseBrandingList)
      }],
      retries: 1
    }
  });
};

export const requestClientActivityList = ({
  context,
  token,
  clientId,
  afterId,
  limit,
  currentActivities,
  locationId,
  showLoading,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading ?? false,
      requests: [{
        route: "client/activity/logs/get",
        token: token,
        payload: { client_id: clientId, after_id: afterId, limit: limit, location_id: locationId },
        handler: (results) => responseHandler(results, parseClientActivityResponse, { currentActivities })
      }],
    }
  });
}

export const requestCaptivePortal = ({
  context,
  token,
  clientId,
  routerId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "captive_portal/preview/get",
        token: token,
        payload: {
          client_id: clientId,
          router_id: routerId
        },
        handler: (results) => responseHandler(results, parseCaptivePortalResponse)
      }]
    }
  });
};

export const requestCaptivePortalCreate = ({
  context,
  token,
  itemIype,
  itemId,
  layoutId,
  navigate,
  completion
}) => {
  const typeId = `${itemIype}_id`;
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "captive_portal/create",
        token: token,
        payload: { [typeId]: itemId, layout_id: layoutId },
        handler: (results) => responseHandler(results, parseClientResponse),
      }]
    }
  });
};

export const requestCaptivePortalDelete = ({
  context,
  token,
  captivePortalId,
  defaultCaptivePortalId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "captive_portal/delete",
        token: token,
        payload: {
          captive_portal_id: captivePortalId,
          default_captive_portal_id: defaultCaptivePortalId
        },
        handler: (results) => responseHandler(results, parseClientResponse),
      }]
    }
  });
};

export const requestCaptivePortalGenerateName =  ({
  context,
  token,
  clientId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "captive_portal/name/generate",
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseCaptivePortalGeneratedName),
      }]
    }
  });
};

export const requestCaptivePortalLayout = ({
  context,
  token,
  layoutId,
  includeConfig = true,
  includeImages = false,
  includeName = false,
  recommendedName,
  clientId,
  clientNameId,
  contextKey = "captivePortal",
  layoutKey = "layout",
  clientKey = "client",
  showLoading = true,
  navigate,
  completion
}) => {
  // decide which requests to make
  const requests = [{
    route: "captive_portal/layout/get",
    token: token,
    payload: {
      layout_id: layoutId,
      include_layout_config: includeConfig
    },
    handler: (results) => responseHandler(
      results,
      parseCaptivePortalLayoutResponse,
      { contextKey, key: layoutKey }
    )
  }];

  if (clientId != null) {
    requests.push({
      route: "client/get",
      token: token,
      payload: {
        client_id: clientId,
        include_accounts: false,
        include_captive_portal_schedules: false
      },
      handler: (results) => responseHandler(
        results,
        parseClientResponse,
        { contextKey, key: clientKey }
      )
    });
  }

  if (includeImages) {
    requests.push({
      route: "captive_portal/image/get/all",
      token: token,
      payload: { client_id: clientId },
      handler: (results) => responseHandler(results, parseCaptivePortalAllImageResponse)
    });
  }

  if (includeName) {
    requests.push({
      route: "captive_portal/name/generate",
      token: token,
      payload: { client_id: clientNameId, recommended_name: recommendedName },
      handler: (results) => responseHandler(results, parseCaptivePortalGeneratedName),
    });
  }

  // make requests
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: requests
    }
  });
};

export const requestAllCaptivePortalLayouts = ({
  context,
  token,
  clientId,
  includeGlobal = false,
  includeConfig = false,
  includeImages = true,
  includeName = false,
  itemType,
  itemId,
  contextKey,
  navigate,
  completion
}) => {
  // build request list
  const requests = [];

  if (itemType && itemId) {
    const itemKey = `${itemType}_id`;
    requests.push({
      route: "client/gdpr/required/get",
      token: token,
      payload: { client_id: clientId, [itemKey]: itemId },
      handler: (results) => responseHandler(results, parseGdprRequiredResponse),
    });
  }

  requests.push({
    route: "captive_portal/layout/get/all",
    token: token,
    payload: {
      client_id: clientId,
      layout_send_globals: includeGlobal,
      include_layout_config: includeConfig
    },
    handler: (results) => responseHandler(
      results,
      parseCaptivePortalAllLayoutResponse,
      { contextKey }
    )
  });

  if (includeImages) {
    requests.push({
      route: "captive_portal/image/get/all",
      token: token,
      payload: {client_id: clientId},
      handler: (results) => responseHandler(
        results,
        parseCaptivePortalAllImageResponse,
        { contextKey }
      )
    });
  }

  if (includeName) {
    requests.push({
      route: "captive_portal/name/generate",
      token: token,
      payload: { client_id: clientId },
      handler: (results) => responseHandler(results, parseCaptivePortalGeneratedName),
    });
  }

  // make requests
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: requests,
      retries: 1
    }
  });
};

export const requestProvisionInfo = ({
  context,
  token,
  clientId,
  brandId,
  contextKey,
  navigate,
  completion
}) => {
  // build request list
  const requests = [{
    route: "branding/get/all",
    payload: { id: brandId },
    token: token,
    handler: (results) => responseHandler(results, parseBrandingList)
  },
  {
    route: "account/get/all",
    token: token,
    payload: { client_id: null },
    handler: (results) => responseHandler(results, parseAllAccountsResponse)
  }, 
  {
    route: "captive_portal/layout/get/all",
    token: token,
    payload: {
      client_id: clientId,
      layout_send_globals: false,
      include_layout_config: false,
    },
    handler: (results) => responseHandler(
      results,
      parseCaptivePortalAllLayoutResponse,
      { contextKey }
    )
  }];

  // make requests
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: requests,
      retries: 1
    }
  });
};

export const requestSaveCaptivePortalLayout = ({
  context,
  token,
  id,
  name,
  config,
  localization,
  postLogin,
  tos,
  verify,
  gdpr,
  splashScreen,
  clientId,
  locationId,
  routerId,
  preview = false,
  navigate,
  completion
}) => {
  // prepare payload
  const payload = {
    client_id: clientId,
    location_id: locationId,
    router_id: routerId,
    layout_id: preview ? null : id,
    layout_name: name,
    layout_config: config,
    layout_language: localization ?? "",
    layout_post_login_config: null,
    req_email_validation: verify?.email ?? false,
    req_sms_validation: verify?.sms ?? false,
    layout_tos_config: null,
    layout_gdpr_config: null,
  };
  
  if (postLogin != null) {
    payload.layout_post_login_config = {
      type: convertPostLoginType(postLogin.type),
      enabled: postLogin.postLoginEnabled,
      facebook_like_url: postLogin.facebookUrl ?? "",
      instagram_profile: postLogin.instagramProfile ?? "",
      default_html: postLogin.default ?? "",
      embedded_video_url: postLogin.videoUrl ?? "",
      html: postLogin.html ?? "",
      redirect_url: postLogin.redirectUrl ?? "",
      redirect_delay: postLogin.delay,
      enable_redirect: postLogin.redirectEnabled,
    };
  }
  if (tos != null) {
    payload.layout_tos_config = { ...tos };
  }
  if (gdpr != null) {
    payload.layout_gdpr_config = { ...gdpr };
  }
  if (splashScreen != null) {
    payload.splash_screen_image_id = splashScreen.setId;
    payload.splash_screen_enabled = splashScreen.enabled;
  }

  // decide on endpoint
  const previewUpdateRoute = "captive_portal/layout/preview/update";
  const createRoute = "captive_portal/client/create";
  const updateRoute = "captive_portal/layout/update";

  const route = preview ? previewUpdateRoute : (
    (clientId && !id) ? createRoute : updateRoute
  );

  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: route,
        token: token,
        payload: payload,
        handler: (results) => responseHandler(results, parseSaveCaptivePortalLayoutResponse)
      }]
    }
  });
};

export const requestDuplicateCaptivePortalLayout = ({
  context,
  token,
  layoutId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "captive_portal/layout/duplicate",
        token: token,
        payload: { layout_id: layoutId },
        handler: (results) => responseHandler(results, parseCaptivePortalLayoutResponse)
      }]
    }
  });
};

export const requestDeleteCaptivePortalLayout = ({
  context,
  token,
  layoutId,
  force = false,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "captive_portal/layout/delete",
        token: token,
        payload: {
          layout_id: layoutId,
          force: force
        },
        handler: (results) => responseHandler(results, parseCaptivePortalLayoutDeleteResponse)
      }]
    }
  });
};

export const requestUploadCaptivePortalMedia = ({
  context,
  token,
  logo,
  splashScreen,
  layoutId,
  clientId,
  preview = false,
  navigate,
  completion
}) => {
  // prepare form
  const formData = new FormData();

  const addMedia = (type, media) => {
    // check media for null
    if (!media) {
      return;
    }

    // add media and key to form
    const key = `${type}-file`;
    formData.append(key, media);
    formData.append(type, JSON.stringify({
      name: media.name,
      description: `captive portal ${media}`,
      key: key
    }));
  };

  formData.append("client_id", clientId ?? "captive_portal");
  formData.append("layout_id", layoutId);
  addMedia("logo", logo);
  addMedia("splash_screen", splashScreen);
  if (preview) {
    formData.append("preview", "True");
  }

  // make request
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "captive_portal/client/image/upload",
        token: token,
        form: formData,
        handler: (results) => responseHandler(results)
      }]
    }
  });  
};

export const requestUpdateCaptivePortalMedia = ({
  context,
  token,
  id,
  name,
  clientId,
  contextKey,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "captive_portal/image/update",
        token: token,
        payload: { id: id, name: name, client_id: clientId },
        handler: (results) => responseHandler(
          results,
          parseCaptivePortalAllImageResponse,
          { contextKey }
        )
      }]
    }
  });
};

export const requestDeleteCaptivePortalMedia = ({
  context,
  token,
  id,
  clientId,
  contextKey,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "captive_portal/image/delete",
        token: token,
        payload: { id: id, client_id: clientId },
        handler: (results) => responseHandler(
          results,
          parseCaptivePortalAllImageResponse,
          { contextKey }
        )
      }]
    }
  });
};


export const requestCaptivePortalPostLoginPreview = ({
  context,
  token,
  type,
  language,
  navigate,
  completion
}) => {
  const payload = { type: convertPostLoginType(type), lang: language };
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "router/hotspot/post_login/template.html",
        token: token,
        payload: payload,
        handler: (results) => responseHandler(
          results,
          parsePostLoginPreviewResponse,
          { type: type }
        )
      }]
    }
  });
}

export const requestUploadCaptivePortalImage = ({
  context,
  token,
  file,
  name,
  type,
  clientId,
  navigate,
  completion
}) => {
  // create form
  const formData = new FormData();
  formData.append("name", name);
  formData.append("file", file);
  formData.append("type", type);
  if (clientId) {
    formData.append("client_id", clientId);
  }

  // make request
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "captive_portal/image/upload",
        token: token,
        form: formData,
        handler: (results) => responseHandler(results, parseCaptivePortalAllImageResponse)
      }]
    }
  });
};

export const requestRouterConfigScript = ({
  context,
  token,
  clientId,
  macAddress,
  showLoading = true,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "router/config/get",
        token: token,
        payload: {
          client_id: clientId,
          mac_address: macAddress
        },
        handler: (results) => responseHandler(results, parseRouterFileResponse, { key: "script" })
      }]
    }
  });
};

export const requestRouterVirtualConfigScript = ({
  context,
  token,
  clientId,
  macAddress,
  showLoading = true,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "router/config/virtual/get",
        token: token,
        payload: {
          client_id: clientId,
          mac_address: macAddress
        },
        handler: (results) => responseHandler(results, parseRouterFileResponse, { key: "script" })
      }]
    }
  });
};

export const requestRouterFilePackage = ({
  context,
  token,
  clientId,
  macAddress,
  showLoading = true,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "router/package/get",
        token: token,
        payload: {
          client_id: clientId,
          mac_address: macAddress
        },
        handler: (results) => responseHandler(results, parseRouterFileResponse, { key: "package" })
      }]
    }
  });
};

export const requestRouterFileVirtualPackage = ({
  context,
  token,
  clientId,
  macAddress,
  showLoading = true,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "router/package/virtual/get",
        token: token,
        payload: {
          client_id: clientId,
          mac_address: macAddress
        },
        handler: (results) => responseHandler(results, parseRouterFileResponse, { key: "package" })
      }]
    }
  });
};

export const requestUpdateAccount = ({
  context,
  token,
  userId,
  userConfig,
  respondAllUsers = false,
  respondClientUsers = null,
  respondUpdatedUser = true,
  navigate,
  completion
}) => {
  // prepare payload
  const payload = {
    // account data
    id: userId,
    admin: userConfig.admin,
    sub_admin: userConfig.subAdmin,
    client_id: userConfig.clientId,
    sub_admin_client_id_list: userConfig.clientIdList,
    sub_admin_brand_id_list: userConfig.brandIdList,
    username: userConfig.username,
    password: userConfig.password,
    first_name: userConfig.firstName,
    last_name: userConfig.lastName,
    permission_edit_templates: userConfig.editTemplates,
    permission_edit_layouts: userConfig.editLayouts,
    permission_provision_business: userConfig.provisionBusiness,
    brand_id: userConfig.brand_id,
    use_local_timezone: userConfig.useLocalTimezone,
    timezone: userConfig.timezone,

    // request flags
    respond_all_users: respondAllUsers,
    respond_client_users: respondClientUsers,
    respond_updated_user: respondUpdatedUser,
  };

  // decide on handler
  let handler = null;
  if (respondAllUsers || respondClientUsers) {
    handler = parseAllAccountsResponse
  }
  else if (respondUpdatedUser) {
    handler = parseAccountResponse;
  }

  // make request
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "account/update",
        token: token,
        payload: payload,
        handler: (results) => responseHandler(results, handler)
      }]
    }
  });
};

export const requestDeleteAccount = ({
  context,
  token,
  userId,
  respondAllUsers,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "account/delete",
        token: token,
        payload: {
          user_id: userId,
          respond_all_users: respondAllUsers
        },
        handler: (results) => responseHandler(
          results, respondAllUsers ? parseAllAccountsResponse : null
        )
      }]
    }
  });
};

export const requestAccount = ({
  context,
  token,
  userId,
  username,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "account/get",
        token: token,
        payload: {
          user_id: userId,
          username: username
        },
        handler: (results) => responseHandler(results, parseAccountResponse)
      }]
    }
  });
}

export const requestAllAccounts = ({
  context,
  token,
  clientId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "account/get/all",
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseAllAccountsResponse)
      }],
      retries: 1
    }
  });
};

export const requestClientUsers = ({
  context,
  token,
  clientId,
  navigate,
  showLoading=true,
  limit=null,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "stats/users/stream/get",
        token: token,
        payload: { client_id: clientId, limit: limit },
        handler: (results) => responseHandler(results, parseClientUsersResponse)
      }],
      retries: 1
    }
  });
};

export const requestGuestProfile = ({
  context,
  token,
  clientId,
  guestId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "stats/profile/get",
        token: token,
        payload: { client_id: clientId, user_id: guestId },
        handler: (results) => responseHandler(results, parseGuestProfileResponse)
      }],
      retries: 1
    }
  });
};

export const requestFilterMarketingBroadcastUsers = ({
  context,
  token,
  clientId,
  filter,
  showLoading = true,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "filter/marketing/client/guests",
        token: token,
        payload: { client_id: clientId, filter: filter },
        handler: (results) => responseHandler(results, parseFilterMarketingBroadcastClientGuestsResponse)
      }],
      retries: 1
    }
  });
}

export const requestClientTemplate = ({
  context,
  token,
  clientId,
  locationId,
  settingsPage = "template",
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "client/template/get",
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseTemplateResponse, { settingsPage })
      }]
    }
  });
};

export const requestClientData = ({
  context,
  token,
  clientId,
  locationId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "dashboard/client/stats",
        token: token,
        payload: { client_id: clientId, location_id: locationId },
        handler: (results) => responseHandler(results, parseClientStatsResponse)
      }],
    }
  });
};

export const requestClientDataCharts = ({
  context,
  token,
  clientId,
  locationId,
  chartTypes,
  reprocess,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandlerAsync(results, completion),
      showLoading: false,
      async: true,
      requests: chartTypes.map((i) => {
        if (i[0] !== "liveActivityFeed") {
          return {
            route: "dashboard/client/chart",
            token: token,
            payload: { client_id: clientId, location_id: locationId , charts: i, reprocess: reprocess },
            async: true,
            handler: (results) => responseHandler(results, parseClientStatsResponse, { locationId })
          }
        } else {
          // Live Activity Feed
          return {
            route: "client/activity/logs/get",
            token: token,
            payload: { client_id: clientId, limit: 100, location_id: locationId },
            handler: (results) => responseHandler(results, parseClientActivityResponse, {})
          }
        } 
      })
    }
  });
}

export const requestClientInfoAndTemplate = ({
  context,
  token,
  clientId,
  locationId,
  settingsPage,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "client/get",
        token: token,
        payload: {
          client_id: clientId,
          include_accounts: false,
          include_images: false,
          include_captive_portals: false,
          include_captive_portal_layouts: false,
          include_captive_portal_schedules: false
        },
        handler: (results) => responseHandler(results, parseClientResponse, { settingsPage })
      },{
        route: "client/template/get",
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseTemplateResponse)
      },/*{
        route: "dashboard/client/stats",
        token: token,
        payload: { client_id: clientId, location_id: locationId },
        handler: (results) => responseHandler(results, parseClientStatsResponse, { locationId })
      }*/],
    }
  });
};

export const requestProvisionClient = ({
  context,
  token,
  payload,
  showLoading = true,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "client/register",
        token: token,
        payload: payload,
        handler: (results) => responseHandler(
          results, parseClientResponse,
          { contextKey: "provisioning", key: "responseClient" }
        ),
      }]
    }
  });
};

export const requestVerifyNewClientName = ({
  context,
  token,
  name,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "client/name/verify",
        token: token,
        payload: { name: name },
        handler: (results) => responseHandler(results, parseExistsResponse)
      }]
    }
  });
};

export const requestVerifyLocationAddress = ({
  context,
  token,
  name,
  address,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "client/address/verify",
        token: token,
        payload: {
          location: {
            name: name,
            street: address.street,
            city: address.city,
            state: address.state,
            zip: address.zip,
            country: address.country,
          }
        },
        handler: (results) => responseHandler(results, parseLocationAddressValid)
      }]
    }
  });
};

export const requestUploadClientImage = ({
  context,
  token,
  form,
  respondClient = true,
  showLoading = true,
  navigate,
  completion
}) => {
  if (respondClient) {
    form.append("respond_client", true);
  }

  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "client/image/upload",
        token: token,
        form: form,
        handler: (results) => responseHandler(results, respondClient ? parseClientResponse : null)
      }]
    }
  });
};

export const requestDeleteClient = ({context, token, clientId, navigate, completion}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "client/delete",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results)
      }]
    }
  });
};

export const requestUpdateClient = ({
  context,
  token,
  client,
  respondClient = true,
  showLoading = true,
  navigate,
  completion
}) => {
  // build payload
  const payload = {
    id: client.id,
    name: client.name,
    description: client.description,
    website_url: client.website,
    email: client.email,
    phone_number: client.phoneNumber,
    product_and_services: client.productAndServices,
    vat_tax_id: client.taxId,
    brand_id: client.brandId,
    address: client.address ? {
      street: client.address.street,
      city: client.address.city,
      state: client.address.state,
      zip: client.address.zip,
      country: client.address.country,
      timezone: client.address.timezone === useLocalTimezone ? null : client.address.timezone,
      use_local_timezone: client.address.timezone === useLocalTimezone
    } : {},
    req_email_validation: client.rev,
    https_hotspot_enabled: client.hhe,
    hotspot_redirect_url: client.hru,
    router_idle_timeout: client.rit,
    respond_client: respondClient,
  };

  // make request
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "client/update",
        token: token,
        payload: payload,
        handler: (results) => responseHandler(results, respondClient ? parseClientResponse : parseIdResponse)
      }]
    }
  });
};

export const requestUpdateLocation = ({
  context,
  token,
  clientId,
  location,
  respondClient = true,
  showLoading = true,
  navigate,
  completion
}) => {
  // build payload
  const payload = {
    client_id: clientId,
    id: location.id,
    name: location.name,
    description: location.description,
    website_url: location.website,
    email: location.email,
    phone_number: location.phoneNumber,
    google_review_search: location.googleReviewSearch,
    address: location.address ? {
      street: location.address.street,
      city: location.address.city,
      state: location.address.state,
      zip: location.address.zip,
      country: location.address.country,
      timezone: location.address.timezone === useLocalTimezone ? null : location.address.timezone,
      use_local_timezone: location.address.timezone === useLocalTimezone
    } : {},
    req_email_validation: location.rev,
    https_hotspot_enabled: location.hhe,
    hotspot_redirect_url: location.hru,
    router_idle_timeout: location.rit,
    respond_client: respondClient,
  };

  // make request
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "client/location/update",
        token: token,
        payload: payload,
        handler: (results) => responseHandler(results, respondClient ? parseClientResponse : parseIdResponse)
      }]
    }
  });
};

export const requestDeleteLocation = ({
  context,
  token,
  clientId,
  locationId,
  respondClient = true,
  showLoading = true,
  navigate,
  completion
}) => {
  // build payload
  const payload = {
    client_id: clientId,
    location_id: locationId,
    respond_client: respondClient,
  };

  // make request
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "client/location/delete",
        token: token,
        payload: payload,
        handler: (results) => responseHandler(results, respondClient ? parseClientResponse : null)
      }]
    }
  });
};

export const requestUpdateRouter = ({
  context,
  token,
  clientId,
  locationId,
  router,
  respondClient = true,
  showLoading = true,
  contextKey,
  key,
  navigate,
  completion
}) => {
  // build payload
  const payload = {
    client_id: clientId,
    location_id: locationId,
    id: router.id,
    mac_address: router.macAddress ?? router.nasClientMacAddress,
    description: router.description,
    req_email_validation: router.requireEmailValidation ?? router.rev,
    req_sms_validation: router.requireSmsValidation ?? router.rsv,
    https_hotspot_enabled: router.httpsHotspotEnabled ?? router.hhe,
    hotspot_redirect_url: router.hotspotRedirectUrl ?? router.hru,
    router_idle_timeout: router.routerIdleTimeout ?? router.rit,
    guest_session_timeout: router.guestSessionTimeout,
    respond_client: respondClient,
  };

  // make request
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "client/router/update",
        token: token,
        payload: payload,
        handler: (results) => responseHandler(
          results,
          respondClient ? parseClientResponse : parseIdResponse,
          { contextKey: contextKey, key: key }
        )
      }]
    }
  });
};

export const requestDeleteRouter = ({
  context,
  token,
  clientId,
  routerId,
  respondClient = true,
  showLoading = true,
  navigate,
  completion
}) => {
  // build payload
  const payload = {
    client_id: clientId,
    nas_client_id: routerId,
    respond_client: respondClient,
  };

  // make request
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        route: "client/router/delete",
        token: token,
        payload: payload,
        handler: (results) => responseHandler(results, respondClient ? parseClientResponse : null)
      }]
    }
  });
};

export const requestUpdateCaptivePortalSchedule = ({
  context,
  token,
  clientId,
  captivePortals,
  includeCaptivePortalLayouts = false,
  navigate,
  completion
}) => {
  // build payload
  const payload = {
    client_id: clientId,
    include_captive_portal_layouts: includeCaptivePortalLayouts,
    captive_portals: captivePortals.map((captivePortal) => {
      const cpSchedule = captivePortal.schedule;
      const schedule = cpSchedule != null ? {
        start_day: cpSchedule.startDay,
        start_month: cpSchedule.startMonth,
        end_day: cpSchedule.endDay,
        end_month: cpSchedule.endMonth,
        restrict_weekday: cpSchedule.restrictWeekday,
      } : null;
      return {
        id: captivePortal.id,
        default: captivePortal.default,
        schedule: schedule
      };
    })
  };

  // make request
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "captive_portal/schedule/update",
        token: token,
        payload: payload,
        handler: (results) => responseHandler(results, parseClientResponse),
      }]
    }
  });
};

export const requestAllRouterStatuses = ({
  context,
  token,
  showLoading = true,
  clearSelectedRouter = false,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/routers/status/all",
        token: token,
        payload: {},
        handler: (results) => responseHandler(results, parseAllRouterStatusesResponse, { clearSelectedRouter })
      }],
      retries: 1
    }
  });
};

export const requestRouterConnectScript = ({
  context,
  token,
  ipAddress,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "resources/router/ssh",
        token: token,
        payload: {
          ip_address: ipAddress
        },
        handler: (results) => responseHandler(results, parseRouterScriptFileResponse, { key: "script" })
      }]
    }
  });
};

export const requestVpnServerCertificate = ({
  context,
  token,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "vpn/server/cert/get",
        token: token,
        payload: {},
        handler: (results) => responseHandler(results, parseServerFileResponse, { key: "certificate" })
      }]
    }
  });
};

export const requestOvpnClientConfiguration = ({
  context,
  token,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "vpn/ovpn/config/get",
        token: token,
        payload: {},
        handler: (results) => responseHandler(results, parseServerFileResponse, { key: "configuration" })
      }]
    }
  });
};

export const requestRouterConfiguration = ({
  context,
  token,
  clientId,
  macAddress,
  showLoading = true,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: showLoading,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/status/get",
        token: token,
        payload: { nas_client_mac_address: macAddress },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      },{
        route: "client/get",
        token: token,
        payload: {
          client_id: clientId,
          include_accounts: false,
          include_images: false,
          include_captive_portals: false,
          include_captive_portal_layouts: false,
          include_captive_portal_schedules: false
        },
        handler: (results) => responseHandler(results, parseClientResponse, { contextKey: "router", key: "clientInfo" })
      }]
    }
  });
}

export const requestRouterConfigSsidUpdate = ({
  context,
  token,
  nasClientMacAddress,
  wirelessInterface,
  newSsid,
  virtual=false,
  hotspot=true,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/ssid/update",
        token: token,
        payload: {
          sync_location: syncLocation,
          nas_client_mac_address: nasClientMacAddress,
          interface: wirelessInterface,
          ssid: newSsid,
          virtual: virtual,
          hotspot: hotspot,
        },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      }]
    }
  });
}

export const requestRouterConfigFrequencyUpdate = ({
  context,
  token,
  nasClientMacAddress,
  wirelessInterface,
  enableChannelAutoOptimizer,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/frequency/update",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          interface: wirelessInterface,
          sync_location: syncLocation,
        },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      }]
    }
  });
}

export const requestRouterConfigEnableChannelAutoOptimizerUpdate = ({
  context,
  token,
  nasClientMacAddress,
  wirelessInterface,
  enableChannelAutoOptimizer,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/enable_channel_auto_optimizer/update",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          enable_channel_auto_optimizer: enableChannelAutoOptimizer,
          sync_location: syncLocation,
        },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      }]
    }
  });
}

export const requestRouterChannelSelectionConfigUpdate = ({
  context,
  token,
  config,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "router/channel/selection/config/update",
        token: token,
        payload: {
          configuration: { ...config },
        },
        handler: (results) => responseHandler(results, parseChannelAutoOptimizationResponse)
      }]
    }
  });
}

export const requestRouterConfigChannelWidthUpdate = ({
  context,
  token,
  nasClientMacAddress,
  wirelessInterface,
  newChannelWidth,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/channel_width/update",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          interface: {
            id: wirelessInterface.id,
            channel_width: newChannelWidth,
          }
        },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      }]
    }
  });
}


export const requestRouterConfigHotspotUserProfileUpdate = ({
  context,
  token,
  nasClientMacAddress,
  profile,
  idleTimeout,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/hotspot/user/profile/update",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          profile: {
            id: profile.id,
            name: profile.name,
            idle_timeout: idleTimeout,
            keepalive_timeout: idleTimeout,
          },
          sync_location: syncLocation,
        },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      }]
    }
  });
}

export const requestRouterConfigHotspotUserProfileRateLimitUpdate = ({
  context,
  token,
  nasClientMacAddress,
  profile,
  rateLimit,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/hotspot/user/profile/rate_limit/update",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          profile: {
            id: profile.id,
            name: profile.name,
            rate_limit: rateLimit,
          },
          sync_location: syncLocation,
        },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      }]
    }
  });
}

export const requestRouterConfigWifiScheduleCreate = ({
  context,
  token,
  nasClientMacAddress,
  schedules,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/wifi/schedule/create",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          schedules: schedules,
          sync_location: syncLocation
        },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      }]
    }
  });
}


export const requestRouterConfigWifiScheduleUpdate = ({
  context,
  token,
  nasClientMacAddress,
  schedules,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/wifi/schedule/update",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          schedules: schedules,
          sync_location: syncLocation,
        },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      }]
    }
  });
}

export const requestRouterConfigWifiScheduleDelete = ({
  context,
  token,
  nasClientMacAddress,
  schedules,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/wifi/schedule/delete",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          schedules: schedules,
          sync_location: syncLocation,
        },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      }]
    }
  });
}

export const requestRouterConfigReboot = ({
  context,
  token,
  nasClientMacAddress,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/reboot",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          sync_location: syncLocation,
        },
        handler: (results) => responseHandler(results)
      }]
    }
  });
}

export const requestRouterConfigHotspotUpdate = ({
  context,
  token,
  nasClientMacAddress,
  hotspot,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/hotspot/update",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          hotspot: hotspot,
          sync_location: syncLocation,
        },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      }]
    }
  });
}

export const requestRouterConfigHotspotProfileUpdate = ({
  context,
  token,
  nasClientMacAddress,
  clientId,
  httpsHotspotEnabled,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/hotspot/profile/update",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          client_id: clientId,
          https_hotspot_enabled: httpsHotspotEnabled,
          sync_location: syncLocation,
        },
        handler: (results) => responseHandler(results, parseRouterHttpsHotspotEnabledUpdateResponse)
      }]
    }
  });
}

export const requestRemoteManagerRouterUpdate = ({
  context,
  token,
  nasClientMacAddress,
  clientId,
  guestSessionTimeout,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/update",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          client_id: clientId,
          guest_session_timeout: guestSessionTimeout,
          sync_location: syncLocation,
        },
        handler: (results) => responseHandler(results, parseRemoteManagerRouterResponse)
      }]
    }
  });
}

export const requestRouterConfigDhcpNetworkUpdate = ({
  context,
  token,
  nasClientMacAddress,
  network,
  syncLocation,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/dhcp/network/update",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress,
          dhcp_network: network,
          sync_location: syncLocation,
        },
        handler: (results) => responseHandler(results, parseRouterConfigurationResponse)
      }]
    }
  });
}

export const requestClientUserSendEmails = ({
  context,
  token,
  clientId,
  useDefault,
  subject,
  body,
  users,
  targetId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "sendgrid/users/send_emails",
        token: token,
        payload: {
          client_id: clientId,
          subject: subject,
          body: body,
          users: users,
          use_default: useDefault,
          target_id: targetId,
        },
        handler: (results) => responseHandler(results)
      }]
    }
  });
}


export const requestDisconnectAllHotspotUsers = ({
  context,
  token,
  nasClientMacAddress,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/logout/users/all",
        token: token,
        payload: {
          nas_client_mac_address: nasClientMacAddress
        },
        handler: (results) => responseHandler(results, parseRouterDisconnectUserResponse)
      }]
    }
  });
}

export const requestDisconnectHotspotUser = ({
  context,
  token,
  nasClientMacAddress,
  id,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        server: "remoteManager",
        route: "remote_manager/router/logout/user",
        token: token,
        payload: {
          id: id,
          nas_client_mac_address: nasClientMacAddress
        },
        handler: (results) => responseHandler(results, parseRouterDisconnectUserResponse)
      }]
    }
  });
}


export const requestAllSendgridConfig = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [
      {
        route: "sendgrid/credentials/get/all",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parseSendgridConfigurations)
      }]
    }
  })
}

export const requestSendgridConfigUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'sendgrid/credentials/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseSendgridConfigurations)
      }]
    }
  })
}


export const requestSendgridConfigDelete = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'sendgrid/credentials/delete',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseSendgridConfigurations)
      }]
    }
  })
}


export const requestMarketingHomeInfo = ({
  context,
  token,
  clientId,
  navigate,
  highlightType,
  highlightId,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "marketing/home",
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(
          results,
          parseMarketingHomeResponse,
          { highlightType, highlightId }
        )
      }]
    }
  });
};

export const requestMarketingIntegrationInfo = ({
  context,
  token,
  clientId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [
      {
        route: "mailchimp/config/get",
        token: token,
        payload: {
          client_id: clientId,
        },
        handler: (results) => responseHandler(results, parseMailchimpConfiguration)
      },
      {
        route: "twilio/sms/config/get/all",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parseTwilioSmsConfigurations)
      },
      {
        route: "plivo/sms/config/get/all",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parsePlivoSmsConfigurations)
      },
      {
        route: "turbo_sms/sms/config/get/all",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parseTurboSmsConfigurations)
      },
      {
        route: "constant_contacts/config/get",
        token: token,
        payload: {
          client_id: clientId,
        },
        handler: (results) => responseHandler(results, parseConstantContactConfiguration)
      },
      {
        route: "integrations/config/get",
        token: token,
        payload: {
          client_id: clientId,
        },
        handler: (results) => responseHandler(results, parseIntegrationConfigurations)
      },
      {
        route: "client/get",
        token: token,
        payload: {
          client_id: clientId,
          include_accounts: false,
          include_images: false,
          include_captive_portals: false,
          include_captive_portal_layouts: false,
          include_captive_portal_schedules: false
        },
        handler: (results) => responseHandler(results, parseClientResponse)
      }
    ]
    }
  });
};

export const requestSendgridSender = ({
  context,
  token,
  clientId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "sendgrid/sender/get",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parseClientSendgridSenders)
      }]
    }
  });
}

export const requestSendgridSenderCreate = ({
  context,
  token,
  clientId,
  senderEmail,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "sendgrid/sender/create",
        token: token,
        payload: {
          client_id: clientId,
          from_email: senderEmail,
          from_name: senderEmail,
          reply_to: senderEmail,
          reply_to_name: senderEmail,
          address: " ",
          city: " ",
          country: " ",
        },
        handler: (results) => responseHandler(results, parseClientSendgridSenders)
      }]
    }
  });
}

export const requestSendgridSenderResend = ({
  context,
  token,
  clientId,
  senderId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "sendgrid/sender/resend",
        token: token,
        payload: {
          client_id: clientId,
          sender_id: senderId
        },
        handler: (results) => responseHandler(results, null)
      }]
    }
  });
}

export const requestSendgridSenderDelete = ({
  context,
  token,
  clientId,
  senderId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "sendgrid/sender/delete",
        token: token,
        payload: {
          client_id: clientId,
          sender_id: senderId,
        },
        handler: (results) => responseHandler(results, parseClientSendgridSenders)
      }]
    }
  });
}

export const requestSendgridSenderUpdate = ({
  context,
  token,
  clientId,
  useDefault,
  sender,
  unsubscribeGroup,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [
      {
        route: "sendgrid/sender/update",
        token: token,
        payload: {
          client_id: clientId,
          use_default: useDefault,
          ...sender,
          unsubscribe_group: { ...unsubscribeGroup },
        },
        handler: (results) => responseHandler(results, parseSendgridSender)
      }]
    }
  });
}

export const requestSendgridSuppressionDelete = ({
  context,
  token,
  useDefault,
  clientId,
  unsubscribeGroupId,
  email,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "sendgrid/suppression/delete",
        token: token,
        payload: {
          use_default: useDefault,
          client_id: clientId,
          unsubscribe_group_id: unsubscribeGroupId,
          email: email,
        },
        handler: (results) => responseHandler(results, null)
      }]
    }
  });
}

export const requestSendgridSuppressionDeleteAll = ({
  context,
  token,
  useDefault,
  clientId,
  unsubscribeGroupId,
  navigate,
  completion
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "sendgrid/suppression/delete/all",
        token: token,
        payload: {
          use_default: useDefault,
          client_id: clientId,
          unsubscribe_group_id: unsubscribeGroupId,
        },
        handler: (results) => responseHandler(results, null)
      }]
    }
  });
}

export const requestAllTwilioConfig = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [
      {
        route: "twilio/sms/config/get/all",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parseTwilioSmsConfigurations)
      }]
    }
  })
}

export const requestTwilioConfigUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'twilio/sms/config/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseTwilioSmsConfigurations)
      }]
    }
  })
}


export const requestTwilioSmsConfigDelete = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'twilio/sms/config/delete',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseTwilioSmsConfigurations)
      }]
    }
  })
}

export const requestAllPlivoConfig = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [
      {
        route: "plivo/sms/config/get/all",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parsePlivoSmsConfigurations)
      }]
    }
  })
}

export const requestPlivoConfigUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'plivo/sms/config/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parsePlivoSmsConfigurations)
      }]
    }
  })
}


export const requestPlivoSmsConfigDelete = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'plivo/sms/config/delete',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parsePlivoSmsConfigurations)
      }]
    }
  })
}

export const requestAllTurboSmsConfig = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [
      {
        route: "turbo_sms/sms/config/get/all",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parseTurboSmsConfigurations)
      }]
    }
  })
}

export const requestTurboSmsConfigUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'turbo_sms/sms/config/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseTurboSmsConfigurations)
      }]
    }
  })
}


export const requestTurboSmsConfigDelete = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'turbo_sms/sms/config/delete',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseTurboSmsConfigurations)
      }]
    }
  })
}

export const requestTwilioSmsSendMessage = ({
  context,
  token,
  clientId,
  useDefault,
  message_id,
  users,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "twilio/sms/send_message",
        token: token,
        payload: {
          client_id: clientId,
          users: users,
          use_default: useDefault,
          message_id: message_id,
        },
        handler: (results) => responseHandler(results)
      }]
    }
  });
}


export const requestAllClientEmailCannedMessageGetAll = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [
      {
        route: "client/email/canned_message/get/all",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parseClientEmailCannedMessage)
      }]
    }
  })
}

export const requestClientEmailCannedMessageUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'client/email/canned_message/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseClientEmailCannedMessage)
      }]
    }
  })
}


export const requestClientEmailCannedMessageDelete = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'client/email/canned_message/delete',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseClientEmailCannedMessage)
      }]
    }
  })
}

export const requestClientSmsCannedMessageUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'client/sms/canned_message/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseClientSmsCannedMessage)
      }]
    }
  })
}


export const requestClientSmsCannedMessageDelete = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'client/sms/canned_message/delete',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseClientSmsCannedMessage)
      }]
    }
  })
}

export const requestAllClientSmsCannedMessageGetAll = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [
      {
        route: "client/sms/canned_message/get/all",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parseClientSmsCannedMessage)
      }]
    }
  })
}


export const requestAllMarketingBroadcastsGetAll = ({
  context,
  token,
  clientId,
  automation = false,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: automation ? "automation/get/all" : "broadcast/get/all",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, automation ? parseMarketingAutomations : parseMarketingBroadcasts)
      }]
    }
  })
}

export const requestMarketingBroadcastUpdate = ({
  context,
  token,
  clientId,
  configuration,
  pendingImageUrl,
  automation = false,
  completion
}) => {
  const payload = { client_id: clientId, ...configuration };
  if (pendingImageUrl) {
    payload.pending_images = [ pendingImageUrl ];
  }
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: automation ? "automation/update" : "broadcast/update",
        token: token,
        payload: payload,
        handler: (results) => responseHandler(results, automation ? parseMarketingAutomations : parseMarketingBroadcasts)
      }]
    }
  });
};

export const requestMarketingNameRecommendation = ({
  context,
  token,
  name,
  type,
  clientId,
  completion,
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: `${type}/name/generate`,
        token: token,
        payload: {
          client_id: clientId,
          recommended_name: name,
        },
        handler: (results) => responseHandler(results, parseMarketingNameRecommendation)
      }]
    }
  });
};

export const requestAutomationEnable = ({
  context,
  token,
  clientId,
  automationId,
  enabled,
  mmsId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: "automation/update",
        token: token,
        payload: {
          client_id: clientId,
          id: automationId,
          enabled: enabled,
          mms_image_id: mmsId,
        },
        handler: (results) => responseHandler(results, parseMarketingAutomations)
      }]
    }
  });
};

export const requestMarketingBroadcastDelete = ({
  context,
  token,
  clientId,
  configuration,
  automation = false,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: automation ? "automation/delete" : "broadcast/delete",
        token: token,
        payload: { client_id: clientId, ...configuration},
        handler: (results) => responseHandler(results, automation ? parseMarketingAutomations : parseMarketingBroadcasts)
      }]
    }
  })
}

export const requestMarketingBroadcastImageUpload = ({
  context,
  token,
  clientId,
  broadcastId,
  imageFile,
  automation = false,
  completion
}) => {
  const formData = new FormData();
  formData.append("client_id", clientId);
  formData.append(automation ? "automation_id" : "broadcast_id", broadcastId);
  formData.append("mms_image", JSON.stringify({
    name: imageFile.name, description: "Marketing media", key: "file"
  }));
  formData.append("file", imageFile);

  context({ 
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: `${automation ? "automation" : "broadcast"}/client/image/upload`,
        token: token,
        form: formData,
        handler: (results) => responseHandler(results)
      }]
    }
  })
};

export const requestMarketingCampaigns = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/campaign/get/all',
        token: token,
        payload: { 
          client_id: clientId,
        },
        handler: (results) => responseHandler(results, parseMarketingCampaigns)
      }]
    }
  })
}

export const requestMarketingCampaign = ({
  context,
  token,
  clientId,
  campaignId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/campaign/get/all',
        token: token,
        payload: { 
          client_id: clientId,
          campaign_id: campaignId
        },
        handler: (results) => responseHandler(results, parseMarketingCampaign)
      }]
    }
  })
}


export const requestMarketingCampaignAdd = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/campaign/create',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results)
      }]
    }
  })
}


export const requestMarketingCampaignUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/campaign/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results)
      }]
    }
  })
}


export const requestMarketingCampaignDelete = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/campaign/delete',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results)
      }]
    }
  })
}

export const requestFacebookPixelCreate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'integrations/facebook_pixel/config/create',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseIntegrationConfigurations)
      }]
    }
  })
}

export const requestFacebookPixelUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'integrations/facebook_pixel/config/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseIntegrationConfigurations)
      }]
    }
  })
}


export const requestFacebookPixelDelete = ({
  context,
  token,
  id,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'integrations/facebook_pixel/config/delete',
        token: token,
        payload: { id: id, client_id: clientId },
        handler: (results) => responseHandler(results, parseIntegrationConfigurations)
      }]
    }
  })
}

export const requestGoogleTagCreate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'integrations/google_tag/config/create',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseIntegrationConfigurations)
      }]
    }
  })
}

export const requestGoogleTagUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'integrations/google_tag/config/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseIntegrationConfigurations)
      }]
    }
  })
}


export const requestGoogleTagDelete = ({
  context,
  token,
  id,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'integrations/google_tag/config/delete',
        token: token,
        payload: { id: id, client_id: clientId },
        handler: (results) => responseHandler(results, parseIntegrationConfigurations)
      }]
    }
  })
}

export const requestMailchimpCreate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'mailchimp/config/create',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseMailchimpConfiguration)
      }]
    }
  })
}

export const requestMailchimpUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'mailchimp/config/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseMailchimpConfiguration)
      }]
    }
  })
}


export const requestMailchimpDelete = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'mailchimp/config/delete',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseMailchimpConfiguration)
      }]
    }
  })
}

export const requestMailchimpGet = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'mailchimp/config/get',
        token: token,
        payload: {
          client_id: clientId,
        },
        handler: (results) => responseHandler(results, parseMailchimpConfiguration)
      }]
    }
  })
}


export const requestConstantContactUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'constant_contacts/config/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseConstantContactConfiguration)
      }]
    }
  })
}

export const requestConstantContactGet = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'constant_contacts/config/get',
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseConstantContactConfiguration)
      }]
    }
  })
}

export const requestConstantContactDelete = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'constant_contacts/config/delete',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseConstantContactConfiguration)
      }]
    }
  })
}

export const requestMailchimpContactSync = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'mailchimp/contacts/sync',
        token: token,
        payload: {
          client_id: clientId,
          
        },
        handler: (results) => responseHandler(results, parseMailchimpSyncStatus)
      }]
    }
  })
}

export const requestMailchimpContactSyncStatus = ({
  context,
  token,
  batchId,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'mailchimp/contacts/sync/status',
        token: token,
        payload: {
          client_id: clientId,
          id: batchId,
        },
        handler: (results) => responseHandler(results, parseMailchimpSyncStatus)
      }]
    }
  })
}


export const requestConstantContactContactSync = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'constant_contacts/sync',
        token: token,
        payload: {
          client_id: clientId,
        },
        handler: (results) => responseHandler(results, parseConstantContactConfiguration)
      }]
    }
  })
}

export const requestConstantContactActivityStatus = ({
  context,
  token,
  activityId,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'constant_contacts/activity/refresh',
        token: token,
        payload: {
          client_id: clientId,
          id: activityId,
        },
        handler: (results) => responseHandler(results, parseConstantContactSyncStatus)
      }]
    }
  })
}


export const requestZapierCreate = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'zapier/client/api_key/create',
        token: token,
        payload: {
          client_id: clientId,
        },
        handler: (results) => responseHandler(results, parseZapierConfiguration)
      }]
    }
  })
}

export const requestZapierTopCrm = ({
  context,
  token,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'zapier/crm/get',
        method: "GET",
        token: token,
        handler: (results) => responseHandler(results, parseZapierCrms)
      }]
    }
  })
}

export const requestZapierContactSupport = ({
  context,
  token,
  clientId,
  crms,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'zapier/contact/support',
        token: token,
        payload: {
          client_id: clientId,
          crm: crms
        },
        handler: (results) => responseHandler(results)
      }]
    }
  })
}

export const requestZapierDelete = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'zapier/client/api_key/delete',
        token: token,
        payload: {
          client_id: clientId,
        },
        handler: (results) => responseHandler(results, parseZapierConfiguration)
      }]
    }
  })
}

export const requestZaiperGet = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'zapier/client/api_key/get',
        token: token,
        payload: {
          client_id: clientId,
        },
        handler: (results) => responseHandler(results, parseZapierConfiguration)
      }]
    }
  })
}


// Review Automation

export const requestReviewAutomationAdd = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'review/automation/create',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseReviewAutomationConfig)
      }]
    }
  })
}


export const requestReviewAutomationUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'review/automation/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseReviewAutomationConfig)
      }]
    }
  })
}


export const requestReviewAutomationDelete = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'review/automation/delete',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results)
      }]
    }
  })
}


export const requestReviewAutomationPageAdd = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'review/automation/page/create',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseReviewAutomationPageConfigs)
      }]
    }
  })
}


export const requestReviewAutomationPageUpdate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'review/automation/page/update',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseReviewAutomationPageReviewUpdate)
      }]
    }
  })
}


export const requestReviewAutomationPageDelete = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'review/automation/page/delete',
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseReviewAutomationPageReviewUpdate)
      }]
    }
  })
}

export const requestReviewAutomationReviewSync = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'review/sync/all',
        token: token,
        payload: {
          client_id: clientId,
        },
        handler: (results) => responseHandler(results, parseReviewAutomationReviews)
      }]
    }
  })
}


export const requestReviewAutomationDefaultCampaign = ({
  context,
  token,
  id,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'review/automation/campaign/default/create',
        token: token,
        payload: {
          id: id,
          client_id: clientId,
        },
        handler: (results) => responseHandler(results)
      }]
    }
  })
}

export const requestReviewSearchVerify = ({
  context,
  token,
  reviewSearch,
  location,  
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'review/search/verify',
        token: token,
        payload: {
          review_search: reviewSearch,
          location: location ? ({ 
            name: location.name,
            street: location.street,
            city: location.city,
            state: location.state,
            zip: location.zip,
            country: location.country,
          }) : null,
          
        },
        handler: (results) => responseHandler(results, parseReviewSearchVerify)
      }]
    }
  })
}

export const requestSendMarketingBroadcast = ({
  context,
  token,
  clientId,
  id,
  type,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'broadcast/send',
        token: token,
        payload: { client_id: clientId, id: id, type: type },
        handler: (results) => responseHandler(results, parseSendBroadcast)
      }]
    }
  });
};

export const requestSendMarketingTestBroadcast = ({
  context,
  token,
  clientId,
  id,
  type,
  email,
  phoneNumber,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'broadcast/test/send',
        token: token,
        payload: { client_id: clientId, id: id, type: type, email: email, phone_number: phoneNumber },
        handler: (results) => responseHandler(results, parseErrorMessage)
      }]
    }
  });
};

export const requestSendMarketingTestAutomation = ({
  context,
  token,
  clientId,
  id,
  email,
  phoneNumber,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'automation/test/send',
        token: token,
        payload: { client_id: clientId, id: id, email: email, phone_number: phoneNumber },
        handler: (results) => responseHandler(results, parseErrorMessage)
      }]
    }
  });
};

export const requestAllMarketingBroadcasts = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/broadcast/get/all',
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseAllMarketingBroadcasts)
      }]
    }
  });
};

export const requestUpdateMarketingBroadcast = ({
  context,
  token,
  clientId,
  id,
  type,
  config,
  sendAfterUpdate,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/broadcast/update',
        token: token,
        payload: {
          client_id: clientId,
          id: id,
          type: type,
          config: {
            name: config.name,
            subject: config.subject,
            body: config.body,
            message: config.message,
            sender: config.sender,
            filters: config.filters
          },
          send_after_update: sendAfterUpdate
        },
        handler: (results) => responseHandler(results)
      }]
    }
  });
};

export const requestDeleteMarketingBroadcast = ({
  context,
  token,
  clientId,
  id,
  type,
  responseAllBroadcasts,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/broadcast/delete',
        token: token,
        payload: {
          client_id: clientId,
          id: id,
          type: type,
          response_all_broadcasts: responseAllBroadcasts
        },
        handler: (results) => responseHandler(results, responseAllBroadcasts ? parseAllMarketingBroadcasts : null)
      }]
    }
  });
};

export const requestAllMarketingAutomations = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/automation/get/all',
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseAllMarketingBroadcasts)
      }]
    }
  });
};

export const requestSendgridIntegrationInfo = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/integration/sendgrid/get',
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseSendgridInfoResponse)
      }]
    }
  });
};

export const requestTwilioIntegrationInfo = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/integration/twilio/get',
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseTwilioInfoResponse)
      }]
    }
  });
};

export const requestMailchimpIntegrationInfo = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/integration/mailchimp/get',
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseMailchimpInfoResponse)
      }]
    }
  });
};

export const requestZapierIntegrationInfo = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: 'marketing/integration/zapier/get',
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseZapierInfoResponse)
      }]
    }
  });
};


export const requestMarketingTemplate = ({
  context,
  token,
  templateId,
  copyNameType,
  recommendedName,
  completion
}) => {
  const requests = [{
    route: "marketing/template/get",
    token: token,
    payload: { id: templateId },
    handler: (results) => responseHandler(results, parseMarketingTemplateResponse)
  }];

  if (copyNameType) {
    requests.push({
      route: "marketing/template/name/generate",
      token: token,
      payload: {
        broadcast: copyNameType === "broadcast",
        automation: copyNameType === "automation",
        recommended_name: recommendedName,
      },
      handler: (results) => responseHandler(results, parseMarketingTemplateNameResponse)
    });
  }

  const completionCreate = copyNameType ? mergeCompletionHandler : completionHandler;

  context({
    requestNavigate: {
      handleCompletion: (results) => completionCreate(results, completion),
      showLoading: true,
      requests: requests
    }
  });
};

export const requestMarketingAllTemplates = ({
  context,
  token,
  navigate,
  isAdmin,
  includeSenders=false,
  completion
}) => {
  const requests = [
    {
      route: "marketing/template/get/all",
      token: token,
      payload: {},
      handler: (results) => responseHandler(results, parseMarketingAllTemplateResponse)
    }
  ]

  if (isAdmin) {
    requests.push({
      route: "marketing/limit/get",
      token: token,
      payload: {},
      handler: (results) => responseHandler(results, parseMarketingDefaultLimitResponse)
    })

    if (includeSenders) {
      requests.push({
        route: "sendgrid/sender/get",
        token: token,
        payload: { client_id: "admin" },
        handler: (results) => responseHandler(results, parseClientSendgridSenders)
      })
    }
  }

  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: requests,
    }
  });
};

export const requestCreateMarketingTemplate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "marketing/template/create",
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseMarketingAllTemplateResponse)
      }]
    }
  });
};

export const requestUpdateMarketingTemplate = ({
  context,
  token,
  configuration,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "marketing/template/update",
        token: token,
        payload: configuration,
        handler: (results) => responseHandler(results, parseMarketingAllTemplateResponse)
      }]
    }
  });
};

export const requestDeleteMarketingTemplate = ({
  context,
  token,
  templateId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "marketing/template/delete",
        token: token,
        payload: { id: templateId },
        handler: (results) => responseHandler(results, parseMarketingAllTemplateResponse)
      }]
    }
  });
};

export const requestMarketingLimitUpdate = ({
  context,
  token,
  broadcastSendLimit = null,
  broadcastSmsLimit = null,
  broadcastEmailLimit = null,
  automationEmailLimit = null,
  automationSmsLimit = null,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "marketing/limit/update",
        token: token,
        payload: { 
          marketing_limits: {
            ...(broadcastSendLimit != null) && { broadcast_send_limit: broadcastSendLimit},
            ...(broadcastSmsLimit != null) && { broadcast_sms_limit: broadcastSmsLimit},
            ...(broadcastEmailLimit != null) && { broadcast_email_limit: broadcastEmailLimit},
            ...(automationEmailLimit != null) && { automation_email_limit: automationEmailLimit},
            ...(automationSmsLimit != null) && { automation_sms_limit: automationSmsLimit},
          } 
        },
        handler: (results) => responseHandler(results, parseMarketingDefaultLimitResponse)
      }]
    }
  });
}

export const requestMarketingLimitGet = ({
  context,
  token,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "marketing/limit/get",
        token: token,
        payload: { },
        handler: (results) => responseHandler(results, parseMarketingDefaultLimitResponse)
      }]
    }
  });
}

export const requestMarketingClientLimitGet = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "marketing/client/limit/get",
        token: token,
        payload: { 
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parseMarketingClientLimitResponse)
      }]
    }
  });
}

export const requestMarketingClientLimitUpdate = ({
  context,
  token,
  clientId,
  broadcastSendLimit = null,
  broadcastSmsLimit = null,
  broadcastEmailLimit = null,
  automationEmailLimit = null,
  automationSmsLimit = null,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "marketing/client/limit/update",
        token: token,
        payload: { 
          marketing_limits: {
            client_id: clientId,
            ...(broadcastSendLimit != null) && { broadcast_send_limit: broadcastSendLimit},
            ...(broadcastSmsLimit != null) && { broadcast_sms_limit: broadcastSmsLimit},
            ...(broadcastEmailLimit != null) && { broadcast_email_limit: broadcastEmailLimit},
            ...(automationEmailLimit != null) && { automation_email_limit: automationEmailLimit},
            ...(automationSmsLimit != null) && { automation_sms_limit: automationSmsLimit},
          } 
        },
        handler: (results) => responseHandler(results, parseMarketingClientLimitResponse)
      }]
    }
  });
}

export const requestMarketingClientLimitClear = ({
  context,
  token,
  clientId,
  broadcastSendLimit = null,
  broadcastSmsLimit = null,
  broadcastEmailLimit = null,
  automationEmailLimit = null,
  automationSmsLimit = null,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "marketing/client/limit/clear",
        token: token,
        payload: { 
          marketing_limits: {
            client_id: clientId,
            ...(broadcastSendLimit != null) && { broadcast_send_limit: broadcastSendLimit},
            ...(broadcastSmsLimit != null) && { broadcast_sms_limit: broadcastSmsLimit},
            ...(broadcastEmailLimit != null) && { broadcast_email_limit: broadcastEmailLimit},
            ...(automationEmailLimit != null) && { automation_email_limit: automationEmailLimit},
            ...(automationSmsLimit != null) && { automation_sms_limit: automationSmsLimit},
          } 
        },
        handler: (results) => responseHandler(results, parseMarketingClientLimitResponse)
      }]
    }
  });
}

export const requestMarketingClientLimitDelete = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "marketing/client/limit/delete",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parseMarketingClientLimitResponse)
      }]
    }
  });
}

export const requestMarketingClientLimitSent = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "marketing/client/limit/sent",
        token: token,
        payload: {
          client_id: clientId
        },
        handler: (results) => responseHandler(results, parseMarketingClientLimitSentResponse)
      }]
    }
  });
}

export const requestAIGeneratedImage = ({
  context,
  token,
  prompt,
  showLoading = false,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: showLoading,
      requests: [{
        route: "ai/image/generate",
        token: token,
        payload: { prompt: prompt },
        handler: (results) => responseHandler(results, parseAIGeneratedImage)
      }]
    }
  });
};

export const requestProductAndServices = ({
  context,
  token,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "ai/client/business/product_and_services/update",
        token: token,
        payload: { client_id: clientId },
        handler: (results) => responseHandler(results, parseProductAndServices)
      }]
    }
  });
};

export const requestPayPalSubscriptionRevise = ({
  context,
  token,
  subscriptionId,
  planId,
  quantity,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/billing/subscription/revise",
        token: token,
        payload: { subscription_id: subscriptionId, plan_id: planId, quantity: quantity, client_id: clientId },
        handler: (results) => responseHandler(results, parsePayPalBillingSubscriptionsRevise)
      }]
    }
  });
};

export const requestPayPalSubscriptionPause = ({
  context,
  token,
  subscriptionId,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/billing/subscription/pause",
        token: token,
        payload: { subscription_id: subscriptionId, client_id: clientId },
        handler: (results) => responseHandler(results)
      }]
    }
  });
};


export const requestPayPalSubscriptionActivate = ({
  context,
  token,
  subscriptionId,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/billing/subscription/activate",
        token: token,
        payload: { subscription_id: subscriptionId, client_id: clientId },
        handler: (results) => responseHandler(results)
      }]
    }
  });
};

export const requestPayPalSubscriptionPlans = ({
  context,
  token,
  planId,
  language,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/subscriptions/get",
        payload: { plan_id: planId, language: language, client_id: clientId },
        token: token,
        handler: (results) => responseHandler(results, parsePaypalPlans)
      }]
    }
  });
};

export const requestPayPalDiscountVerify = ({
  context,
  token,
  planId,
  clientId,
  code,
  completion,
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/discount/verify",
        payload: { plan_id: planId, code: code, client_id: clientId },
        token: token,
        handler: (results) => responseHandler(results, parsePaypalDiscount)
      }]
    }
  });
}

export const requestPayPalSubscriptionInvoice = ({
  context,
  token,
  planId,
  quantity,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/subscription/invoice",
        payload: { plan_id: planId, client_id: clientId, quantity: quantity },
        token: token,
        handler: (results) => responseHandler(results)
      }]
    }
  });
};

export const requestPayPalSendInvoice = ({
  context,
  token,
  clientId,
  firstName,
  lastName,
  email,
  recurring,
  timezone,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/invoice/send",
        payload: { client_id: clientId, recipient_first_name: firstName, recipient_last_name: lastName, recipient_email: email, recurring: recurring, timezone: timezone},
        token: token,
        handler: (results) => responseHandler(results)
      }]
    }
  });
};

export const requestPayPalEnableRecurringInvoice = ({
  context,
  token,
  clientId,
  enabled,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/invoice/recurring/update",
        payload: { client_id: clientId, enabled: enabled },
        token: token,
        handler: (results) => responseHandler(results)
      }]
    }
  });
};

export const requestPayPalInvoiceDiscountUpdate = ({
  context,
  token,
  clientId,
  value,
  type,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/invoice/discount/update",
        payload: { client_id: clientId, billing_invoice_discount_value: value, billing_invoice_discount_type: type },
        token: token,
        handler: (results) => responseHandler(results)
      }]
    }
  });
};

export const requestPayPalSubscriptionAdminUpdate = ({
  context,
  token,
  planId,
  quantity,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/admin/subscription/update",
        payload: { plan_id: planId, client_id: clientId, quantity: quantity },
        token: token,
        handler: (results) => responseHandler(results)
      }]
    }
  });
};

export const requestPayPalSubscriptionPlan = ({
  context,
  token,
  planId,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/billing/plan/get",
        payload: { plan_id: planId, client_id: clientId },
        token: token,
        handler: (results) => responseHandler(results, parsePaypalPlan)
      }]
    }
  });
};

export const requestPayPalSubscriptionApproved = ({
  context,
  token,
  subscriptionId,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/billing/subscription/approved",
        payload: { subscription_id: subscriptionId, client_id: clientId },
        token: token,
        handler: (results) => responseHandler(results)
      }]
    }
  });
};

export const requestPayPalSubscriptionSync = ({
  context,
  token,
  subscriptionId,
  clientId,
  completion
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/subscription/sync",
        payload: { subscription_id: subscriptionId, client_id: clientId },
        token: token,
        handler: (results) => responseHandler(results)
      }]
    }
  });
};

export const requestPayPalSubscriptionDiscounts = ({
  context,
  token,
  completion,
  navigate
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/discount/get/all",
        payload: { },
        token: token,
        handler: (results) => responseHandler(results, parsePaypalDiscounts)
      }]
    }
  });
}

export const requestPayPalSubscriptionDiscountUpdate = ({
  context,
  token,
  discount,
  completion,
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/discount/update",
        payload: discount,
        token: token,
        handler: (results) => responseHandler(results, parsePaypalDiscounts)
      }]
    }
  });
}

export const requestPayPalSubscriptionDiscountDelete = ({
  context,
  token,
  id,
  completion,
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "paypal/discount/delete",
        payload: { id: id },
        token: token,
        handler: (results) => responseHandler(results, parsePaypalDiscounts)
      }]
    }
  });
}

export const requestBrandingCreate = ({
  context,
  token,
  payload,
  completion,
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "branding/create",
        payload: payload,
        token: token,
        handler: (results) => responseHandler(results, parseBranding, { domainBrand: false })
      }]
    }
  });
}

export const requestBrandingUpdate = ({
  context,
  token,
  payload,
  domainBrand,
  completion,
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "branding/update",
        payload: payload,
        token: token,
        handler: (results) => responseHandler(results, parseBranding, { domainBrand: domainBrand })
      }]
    }
  });
}

export const requestBrandingSendgridResendVerification = ({
  context,
  token,
  brandId,
  domainBrand,
  completion,
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "branding/sendgrid/resend_verification",
        payload: { id: brandId },
        token: token,
        handler: (results) => responseHandler(results, parseBranding, { domainBrand: domainBrand })
      }]
    }
  });
}

export const requestBrandingDelete = ({
  context,
  token,
  id,
  completion,
}) => {
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "branding/delete",
        payload: { id: id },
        token: token,
        handler: (results) => responseHandler(results, parseErrorMessage)
      }]
    }
  });
}

export const requestBrandingGet = ({
  context,
  token,
  id,
  domainBrand,
  completion,
  navigate
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "branding/get",
        payload: { id: id },
        token: token,
        handler: (results) => responseHandler(results, parseBranding, { domainBrand: domainBrand })
      }]
    }
  });
}

export const requestBrandingGetAll = ({
  context,
  token,
  completion,
  navigate
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "branding/get/all",
        token: token,
        handler: (results) => responseHandler(results, parseBrandingList)
      }]
    }
  });
}

export const requestBrandingAdminGetAll = ({
  context,
  token,
  id,
  completion,
  navigate
}) => {
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),      
      showLoading: true,
      requests: [{
        route: "branding/get/all",
        payload: { id: id },
        token: token,
        handler: (results) => responseHandler(results, parseBrandingList)
      },
      {
        route: "account/get/all",
        token: token,
        payload: { client_id: null },
        handler: (results) => responseHandler(results, parseAllAccountsResponse)
      }]
    }
  });
}

export const requestBrandingImageUpload = ({
  context,
  token,
  file,
  name,
  description,
  brandId,
  type,
  domainBrand,
  navigate,
  completion
}) => {
  // create form
  const formData = new FormData();
  formData.append("name", name);
  formData.append("file", file);
  formData.append("description", description);
  formData.append("brand_id", brandId);

  // make request
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: `branding/${type}/image/upload`,
        token: token,
        form: formData,
        handler: (results) => responseHandler(results, parseBranding, { domainBrand: domainBrand })
      }]
    }
  });
};

export const requestBrandingImageDelete = ({
  context,
  token,
  brandId,
  type,
  domainBrand,
  navigate,
  completion
}) => {
  // make request
  context({
    requestNavigate: {
      handleNavigate: (results) => navigationHandler(results, navigate),
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: `branding/${type}/image/delete`,
        token: token,
        payload: { brand_id: brandId },
        handler: (results) => responseHandler(results, parseBranding, { domainBrand: domainBrand })
      }]
    }
  });
};

export const requestBrandingMergeClient = ({
  context,
  token,
  clientId,
  brandId,
  routers,
  completion
}) => {
  // make request
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: `branding/client/migrate`,
        token: token,
        payload: { id: brandId, client_id: clientId, routers: routers },
        handler: (results) => responseHandler(results, parseClientResponse)
      }]
    }
  });
};

export const requestBrandingClearRouterStatus = ({
  context,
  token,
  clientId,
  routerId,
  completion
}) => {
  // make request
  context({
    requestNavigate: {
      handleCompletion: (results) => completionHandler(results, completion),
      showLoading: true,
      requests: [{
        route: `branding/router/status/clear`,
        token: token,
        payload: { router_id: routerId, client_id: clientId },
        handler: (results) => responseHandler(results, parseClientResponse)
      }]
    }
  });
};
